import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Grid, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { useTranslation } from "react-i18next";
import { LoadingGuard, ShowAccessDeniedMsg, SistaId, useSafeSistaAsync, } from "@sista/library-stasi";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { IdmClientContext } from "@sista/idm-client";
import { SistaChip } from "@sista/sista-chip";
import { Clear } from "@mui/icons-material";
import { AlertsContext, DistaAutocomplete, DistaConfirmDialog, } from "@sista/dista-ui";
import { useSpravaTextuClient } from "../../util/useSpravaTextuClient";
import { useVazbatorClient } from "../../util/useVazbatorClient";
import { useSistaChipDataGetter } from "./SelectVariantUrns";
const iconSize = { width: "20px", height: "20px" };
export const RolesAdministration = ({ tree, canViewRoles, canEditRoles, }) => {
    const { t } = useTranslation("sprava-textu-admin");
    const { loggedAccount } = useContext(IdmClientContext);
    const { showSuccess, showError } = useContext(AlertsContext);
    const spratex = useSpravaTextuClient();
    const vazbator = useVazbatorClient();
    const [getRolesNamesCall, getRolesNamesState] = useSafeSistaAsync(vazbator.findAllRoles);
    const [getAllRolesCall, getAllRolesState] = useSafeSistaAsync(spratex.getRoles);
    const [getAllUsersAsAdminCall, getAllUsersAsAdminState] = useSafeSistaAsync(spratex.getSpravaTextuUsers);
    const [getAllUsersForProviderCall, getAllUsersForProviderState] = useSafeSistaAsync(spratex.getAllUsersByProviderId);
    const [addUserToRoleCall, addUserToRoleState] = useSafeSistaAsync(spratex.addUserInRole);
    const [deleteUserFromRoleCall, deleteUserFromRoleState] = useSafeSistaAsync(spratex.deleteUserRole);
    const providerId = useMemo(() => {
        return loggedAccount.providerId;
    }, [loggedAccount.providerId]);
    const [selectedProviderUrn, setSelectedProviderUrn] = useState(null);
    const getSelectedProviderId = useCallback(() => {
        if (selectedProviderUrn == null)
            return null;
        const id = SistaId.parse(selectedProviderUrn).id.toString();
        return id;
    }, [selectedProviderUrn]);
    useEffect(() => {
        getAllRolesCall({});
        getRolesNamesCall({});
    }, [getAllRolesCall, getRolesNamesCall]);
    useEffect(() => {
        getAllUsersAsAdminCall({
            providerId: getSelectedProviderId() ?? providerId,
        });
        getAllUsersForProviderCall({
            providerId: getSelectedProviderId() ?? providerId,
        });
    }, [
        providerId,
        getAllUsersAsAdminCall,
        getAllUsersForProviderCall,
        getSelectedProviderId,
        selectedProviderUrn,
    ]);
    const findRoleByRoleId = (roleId, allRoles, prefLang) => {
        if (allRoles == null) {
            return "-";
        }
        return allRoles.find((role) => role.role === roleId)?.name[prefLang ? prefLang : "cs"];
    };
    const allProfilesForProvider = useMemo(() => {
        return getAllUsersForProviderState.value || [];
    }, [getAllUsersForProviderState.value]);
    const getAllUsersWithRole = (role, allUsers) => {
        if (role == null || allUsers == null) {
            return [];
        }
        const users = [];
        for (const user of allUsers) {
            if (user.role === role) {
                users.push(user.profileUrn);
            }
        }
        return users;
    };
    const addUserToRoleHandler = useCallback(async (profileUrn, role) => {
        if (profileUrn == null || role == null)
            return;
        const result = await addUserToRoleCall({
            profileUrn: profileUrn,
            role: role,
            providerId: getSelectedProviderId() ?? providerId,
        });
        if (typeof result === "object" && result) {
            showSuccess(t("sprava-textu.rolesAdministration.userAdded", "Uživatel přidán do role"));
            getAllUsersAsAdminCall({
                providerId: getSelectedProviderId() ?? providerId,
            });
        }
        else {
            showError(t("sprava-textu.rolesAdministration.errorAddingUser", "Chyba při přidávání uživatele do role"));
        }
    }, [
        addUserToRoleCall,
        providerId,
        getAllUsersAsAdminCall,
        getSelectedProviderId,
        showError,
        showSuccess,
        t,
    ]);
    const deleteUserFromRoleHandler = useCallback(async (profileUrn, role) => {
        if (profileUrn == null || role == null)
            return;
        try {
            await deleteUserFromRoleCall({
                profileUrn: profileUrn,
                role: role,
                providerId: getSelectedProviderId() ?? providerId,
            });
        }
        catch (error) {
            showError(t("sprava-textu.rolesAdministration.errorRemovingUser", "Chyba při odebírání uživatele z role"));
            setUserToDelete(null);
            return;
        }
        showSuccess(t("sprava-textu.rolesAdministration.userRemoved", "Uživatel odebrán z role"));
        getAllUsersAsAdminCall({
            providerId: getSelectedProviderId() ?? providerId,
        });
        setUserToDelete(null);
    }, [
        deleteUserFromRoleCall,
        providerId,
        getAllUsersAsAdminCall,
        showError,
        showSuccess,
        t,
        getSelectedProviderId,
    ]);
    const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const onCloseHandler = () => {
        setShowDeleteConfirmDialog(false);
        setUserToDelete(null);
    };
    return (_jsx(Stack, { direction: "column", spacing: 2, sx: {
            mb: 5,
            height: "100%",
            width: "100%",
            overflowX: "hidden",
            overflowY: "auto",
        }, children: canViewRoles ? (_jsx(LoadingGuard, { state: getAllUsersAsAdminState, children: _jsx(LoadingGuard, { state: getAllRolesState, children: _jsxs(LoadingGuard, { state: getRolesNamesState, children: [providerId === "tacr" && (_jsx(Grid, { container: true, spacing: 2, children: _jsx(Grid, { item: true, xs: 12, md: 2, children: _jsx(ProviderPicker, { canEditRoles: canEditRoles, tree: tree, providerUrn: selectedProviderUrn ??
                                        new SistaId("providers", providerId).asUrn(), onChange: (newProviderUrn) => {
                                        setSelectedProviderUrn(newProviderUrn);
                                    } }) }) })), getAllRolesState.value != null &&
                            getRolesNamesState.value != null &&
                            getAllRolesState.value.map((role) => (_jsxs(Grid, { container: true, spacing: 2, sx: { alignItems: "left" }, children: [_jsx(Grid, { item: true, xs: 12, md: 12, lg: 3, children: _jsxs(Stack, { direction: "column", spacing: 1, children: [_jsx(Typography, { variant: "body1Hl", children: findRoleByRoleId(role, getRolesNamesState.value, loggedAccount.preferredLanguage) }), _jsx(Typography, { variant: "body1Xs", sx: { fontStyle: "monospace" }, children: role })] }) }), _jsx(Grid, { item: true, xs: 12, md: 12, lg: 9, children: _jsxs(Stack, { direction: "column", spacing: 2, children: [getAllUsersWithRole(role, getAllUsersAsAdminState.value).length === 0 && (_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("sprava-textu.rolesAdministration.noUsersWithRole", "Žádní uživatelé s touto rolí") })), getAllUsersAsAdminState.value &&
                                                    getAllUsersWithRole(role, getAllUsersAsAdminState.value).map((user) => (_jsxs(Grid, { container: true, alignItems: "left", sx: { m: 0, p: 0 }, children: [_jsx(Grid, { item: true, xs: 10, md: 4, children: _jsx(SistaChip, { variant: "small", urn: user, authToken: loggedAccount.authKey }) }), _jsx(Grid, { item: true, xs: 2, md: 2, children: _jsx(Button, { onClick: () => {
                                                                        setUserToDelete({
                                                                            profileUrn: user,
                                                                            role: role,
                                                                        });
                                                                        setShowDeleteConfirmDialog(true);
                                                                    }, disabled: !canEditRoles, children: _jsx(Clear, { sx: iconSize, color: "error" }) }) })] }, user))), showDeleteConfirmDialog && userToDelete != null && (_jsx(DistaConfirmDialog, { onClose: onCloseHandler, open: true, okAction: {
                                                        type: "button",
                                                        children: t("sprava-textu.rolesAdministration.confirmDialog.ok", "Ano"),
                                                        onClick: () => {
                                                            setShowDeleteConfirmDialog(false);
                                                            deleteUserFromRoleHandler(userToDelete.profileUrn, userToDelete.role);
                                                        },
                                                    }, cancelAction: {
                                                        type: "button",
                                                        children: t("sprava-textu.rolesAdministration.confirmDialog.cancel", "Zrušit"),
                                                        onClick: () => onCloseHandler(),
                                                    }, title: t("sprava-textu.rolesAdministration.confirmDialog.title", "Odebrání role uživateli"), children: t("sprava-textu.rolesAdministration.confirmDialog.removeUserRole", "Opravdu chcete odebrat roli uživateli?") })), _jsx(ProfileUrnPicker, { canEditRoles: canEditRoles, allProfiles: allProfilesForProvider, onChange: (selectedProfile) => {
                                                        addUserToRoleHandler(selectedProfile, role);
                                                    }, sx: { width: "15em" } }, role)] }) })] }, role)))] }) }) })) : (_jsx(ShowAccessDeniedMsg, { message: t("sprava-textu.rolesAdministration.accessDenied", "Nemáte oprávnění k zobrazení správy rolí.") })) }));
};
export const ProfileUrnPicker = ({ canEditRoles, allProfiles, sx, onChange, }) => {
    const [profileOptions, setProfileOptions] = useState(null);
    const { t } = useTranslation("sprava-textu");
    const { loggedAccount } = useContext(IdmClientContext);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const chipDataGetter = useSistaChipDataGetter();
    const getChipLabel = useCallback(async (urn) => {
        try {
            const chip = await chipDataGetter(urn, loggedAccount.authKey);
            return chip ? chip.displayName : urn;
        }
        catch (error) {
            //console.error("Error fetching chip label:", error);
            return urn;
        }
    }, [chipDataGetter, loggedAccount.authKey]);
    useEffect(() => {
        if (allProfiles == null)
            return;
        const loadProfileOptions = async () => {
            const options = await Promise.all(allProfiles.map(async (a) => ({
                value: a,
                label: await getChipLabel(a),
            })));
            setProfileOptions(options);
        };
        loadProfileOptions();
    }, [allProfiles, getChipLabel]);
    return (_jsxs(Grid, { container: true, spacing: 1, sx: { m: 0, p: 0, alignItems: "center" }, children: [_jsx(Grid, { item: true, xs: 12, md: 4, sx: { pl: "0px!important" }, children: _jsx(DistaAutocomplete, { label: t("sprava-textu.rolesAdministration.selectUser", "Vybrat uživatele"), size: "small", options: profileOptions ?? [], multiple: false, value: selectedProfile, onChange: (event, value) => {
                        setSelectedProfile(value);
                    }, filterOptions: (options, { inputValue }) => options.filter((item) => item.label.toLowerCase().includes(inputValue.toLowerCase())), renderOption: (props, option) => {
                        return (_createElement("li", { ...props, key: option.value }, option.label));
                    }, loading: profileOptions == null, sx: { minWidth: "10em", maxWidth: "15em" }, disabled: !canEditRoles }) }), _jsx(Grid, { item: true, xs: 12, md: 2, children: _jsx(Button, { variant: "contained", size: "small", onClick: () => {
                        onChange(selectedProfile.value);
                        setSelectedProfile(null);
                    }, disabled: selectedProfile == null, children: t("sprava-textu.rolesAdministration.addUser", "Přidat uživatele") }) })] }));
};
export const ProviderPicker = ({ canEditRoles, tree, providerUrn, onChange, sx, }) => {
    const { t } = useTranslation("sprava-textu");
    const { loggedAccount } = useContext(IdmClientContext);
    const [providerUrnOptions, setProviderUrnOptions] = useState(null);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const chipDataGetter = useSistaChipDataGetter();
    const getChipLabel = useCallback(async (urn) => {
        try {
            const chip = await chipDataGetter(urn, loggedAccount.authKey);
            return chip ? chip.displayName : urn;
        }
        catch (error) {
            //console.error("Error fetching chip label:", error);
            return urn; // Fallback to URN if there's an error
        }
    }, [chipDataGetter, loggedAccount.authKey]);
    useEffect(() => {
        if (!tree)
            return;
        const loadProviderOptions = async () => {
            const options = await Promise.all(tree.map(async (a) => ({
                value: a.urn,
                label: await getChipLabel(a.urn),
            })));
            setProviderUrnOptions(options);
        };
        loadProviderOptions();
    }, [tree, getChipLabel]);
    useEffect(() => {
        if (providerUrn == null) {
            return;
        }
        const selected = providerUrnOptions?.find((option) => option.value === providerUrn);
        setSelectedProvider(selected ?? null);
    }, [providerUrn, providerUrnOptions]);
    return (_jsx(DistaAutocomplete, { label: t("sprava-textu.selectProvider.title", "Poskytovatel"), size: "small", options: providerUrnOptions ?? [], multiple: false, value: selectedProvider, onChange: (event, value) => {
            setSelectedProvider(value);
            onChange(value?.value ?? null);
        }, filterOptions: (options, { inputValue }) => options.filter((item) => {
            return item.label.toLowerCase().includes(inputValue);
        }), loading: providerUrnOptions == null, renderOption: (props, option) => {
            return (_createElement("li", { ...props, key: option.value }, option.label));
        }, sx: { ...sx, width: "15em" }, disabled: !canEditRoles }));
};
